import {customElement, property, state} from "lit/decorators.js";
import {LitElement, html, CSSResult, unsafeCSS} from "lit";
import {provide} from '@lit/context';
import {
  OnlineReservationConfig,
  Messages,
  Store,
} from "../../@types/online-reservation.js";

import '../tooltip/tooltip.js';

import styles from './online-reservation.scss';
import {OpenHvaModalEvent} from "../../@types/dom.js";
import {EntsDrawer} from "../drawer/drawer.js";

import './step-one';
import './step-two';
import './step-three';
import './step-four';
import './step-five';

import { i18nContext } from "./i18nContext.js";
import { AppState, ModelState, SelectionState } from "./states/index.js";
import arrowBackIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_arrow-left.svg';
import {addPresentationToIcon} from "../icon/icon-accessibility.js";
import { track } from '../tracking/generic-tracking-layer.js';
import {DomainEvent} from "@cube/tracking-library-api";

@customElement('ents-online-reservation')
export class OnlineReservation extends LitElement {
  @property({type: Object, reflect: false}) config: OnlineReservationConfig = {
    articleDetailsUrl: '',
    messages: {},
    dataProtectionRegulationLink: '',
    reservationEndpoint: ''
  };

  @provide({context: i18nContext})
  private messages!: Messages;

  @state() private appState = new AppState();
  @state() private modelState = new ModelState();
  @state() private selectionState = new SelectionState();

  constructor() {
    super();
    this.messages = this.config.messages;
  }

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      'ents:hva-modal:open', event => this.handleOpenEvent(event));
    this.messages = this.config.messages;
  }

  private handleOpenEvent(event: OpenHvaModalEvent) {
    this.loadModel(event.detail.articleId);
    this.showDrawer();
  }

  private loadModel(variantId: string) {
    this.appState = this.appState.setLoading();

    const articleDetailsUls = this.config.articleDetailsUrl.replace('VARIANT_ID', variantId);
    fetch(articleDetailsUls)
      .then(response => response.json())
      .then((response) => {
        this.modelState = ModelState.createFromResponse(response);
        this.appState = this.appState.setLoaded();

        // Skip size selection when OneSize or NOSize, then take only size available
        if (this.modelState.shouldSkipToStoreSelection()) {
          const firstSizeId = this.modelState.getFirstAvailableSize();
          if (firstSizeId) {
            this.selectionState = this.selectionState.selectSize(firstSizeId);
          }
        }
      })
      .catch(() => {
        this.appState = this.appState.setLoadedWithError();
      });
  }

  render() {
    return html`
      <ents-drawer id="onlineReservationDrawer" class="ents-or__drawer" fullscreen>
        <div class="ents-or__drawer__content">
          ${this.renderContent()}
        </div>
      </ents-drawer>
    `;
  }

  private renderBackButton() {
    // The first step does not have a back button
    if (this.appState.currentStep === 0 || this.appState.currentStep === 4) {
      return '';
    }

    const handleBack = () => {
      const { skipSizeLayer } = this.modelState;
      if (this.appState.currentStep === 2 && skipSizeLayer) {
        // Without size selection, go directly to the first step
        this.appState = this.appState.goToFirstStep();
      } else {
        // Otherwise just go back one step
        this.appState = this.appState.prevStep();
      }
    };

    return html`
      <a
        class="bdk-link-secondary ents-or__back-button"
        size="m"
        href="#"
        data-test-ents-or-back-button
        aria-label="${this.messages['back']}"
        @click="${handleBack}">
        ${addPresentationToIcon(arrowBackIcon())} ${this.messages['back']}
      </a>
    `;
  }

  handleSuccessfulReservation() {
    this.appState = this.appState.setReservationStatus(true);
    track(this.getDataracking());
    this.appState = this.appState.nextStep();
  }

  handleFailedReservation(errorMessage: string) {
    console.error(errorMessage);
    this.appState = this.appState.setReservationStatus(false);
    this.appState = this.appState.nextStep();
  }

  handleReservationRetry() {
    this.appState = this.appState.prevStep();
  }

  private renderContent() {
    return this.appState.loadingState.isFailed
      ? this.renderError()
      : html`
          ${this.renderBackButton()}
          ${this.renderStep()}
        `;
  }

  private renderError() {
    return html`
      <div class="ents-or__error">
        <p class="ents-copy ents-or__hint ents-or__hint--error">
          ${this.config.messages.error}
        </p>
      </div>
    `;
  }

  private renderStep() {
    const { currentStep, reservationSuccessful } = this.appState;
    const { article, skipSizeLayer, sizesWithStoreStock } = this.modelState;
    const { selectedSize, selectedStore } = this.selectionState;

    return html`
      ${currentStep === 0
        ? html`<step-one
          .skipSizeLayer=${skipSizeLayer}
          .onNext=${() => {
            if(skipSizeLayer) {
              this.appState = this.appState.goToStoreSelectionStep();
            } else {
              this.appState = this.appState.nextStep();
            }
          }}
          .article=${article}>
        </step-one>`
        : ''}
      ${currentStep === 1
        ? html`<step-two
          .sizesWithStoreStock=${sizesWithStoreStock}
          .onNext=${() => this.appState = this.appState.nextStep()}
          .selectSize=${(articleId: string) => this.selectionState = this.selectionState.selectSize(articleId)}
        ></step-two>`
        : ''}
      ${currentStep === 2
        ? html`<step-three
          .selectStore=${(store: Store) => {
            this.selectionState = this.selectionState.selectStore(store);
            this.selectionState.saveSelectedStoreId();
          }}
          .stores=${this.modelState.getStoresForSize(selectedSize)}
          .articleid=${selectedSize}
          .size=${this.modelState.getSizeById(selectedSize)}
          .onNext=${() => this.appState = this.appState.nextStep()}
          .showSizeBadge=${!skipSizeLayer}
        ></step-three>`
        : ''}
      ${currentStep === 3
        ? html`<step-four
          .onNext=${() => this.appState = this.appState.nextStep()}
          .handleSuccessfulReservation=${() => this.handleSuccessfulReservation()}
          .handleFailedReservation=${(errorMessage: string) => this.handleFailedReservation(errorMessage)}
          .config=${{
            articleId: selectedSize,
            size: this.modelState.getSizeById(selectedSize),
            storeLocation: selectedStore,
            dataProtectionRegulationLink: this.config.dataProtectionRegulationLink,
            reservationEndpoint: this.config.reservationEndpoint
          }}
          .showSizeBadge=${!skipSizeLayer}
        ></step-four>`
        :''}
      ${currentStep === 4
        ? html`<step-five
            .onNext=${() => this.hideDrawer()}
            .onRetry=${() => this.handleReservationRetry()}
            .reservationSuccessful=${reservationSuccessful === true}
            .article=${article}
            .showSizeBadge=${!skipSizeLayer}
        ></step-five>`
        : ''}
    `;
  }

  private showDrawer() {
    this.appState = AppState.reset();
    this.selectionState = SelectionState.reset();
    return this.doOnDrawer(drawer => drawer.show());
  }

  private hideDrawer() {
    this.appState = AppState.reset();
    this.selectionState = SelectionState.reset();
    return this.doOnDrawer(drawer => drawer.hide());
  }

  private doOnDrawer(action: (modal: EntsDrawer) => void) {
    const drawer = this.shadowRoot?.querySelector<EntsDrawer>('#onlineReservationDrawer');
    if (drawer) {
      action(drawer);
      return true;
    }
    return false;
  }

  private getDataracking():DomainEvent {
    return {
      event_name: 'click_reserve',
      event_data: {
        reservation_store_name: this.selectionState.selectedStore?.name || ''
      },
      currency: {
        code: 'EUR'
      },
      items: [
        {
          produkt_id: this.modelState.trackingData.produkt_id,
          farb_id: this.modelState.trackingData.farb_id ?? '',
          artikel_id: this.selectionState.selectedSize,
          vertriebsinfo_id: this.modelState.getsalesInformationIdById(this.selectionState.selectedSize),
          syan: this.modelState.trackingData.syan,
          quantity: 1
        }
      ],
      responsible_team_tracking: "ENTS",
      responsible_team_feature: "ENTS",
    };
  }
}
